import { createSelector } from 'reselect';
import { parseTexturedMaterial, randomArr } from 'shared-base';
import { parseTint } from '../utils/color';
import { raw } from './selectors.raw';

export const $project = createSelector([raw.$rawProjects, (_state, id) => id], (projects, id) => {
  return projects[id];
});

export const $currentProject = createSelector(
  raw.$rawCurrentIds,
  raw.$rawProjects,
  (currentIds, projects) => {
    const { projectId } = currentIds;
    return projects[projectId];
  }
);

export const $projectType = createSelector($currentProject, (project) => {
  const { type } = project ?? {};
  return type;
});

export const $currentProjectId = createSelector(raw.$rawCurrentIds, (currentIds) => {
  const { projectId } = currentIds;
  return projectId as string;
});

export const $currentGoldenId = createSelector(raw.$rawCurrentIds, (currentIds) => {
  const { goldenId } = currentIds;
  return goldenId as string;
});

export const $currentGolden = createSelector(
  raw.$rawCurrentIds,
  raw.$rawGoldens,
  (currentIds, goldens) => {
    const { goldenId } = currentIds;
    return goldens[goldenId];
  }
);

export const $editorCommand = createSelector(
  [raw.$rawEditorCommands, (_state, id) => id],
  (commands, id) => {
    return commands[id];
  }
);

export const $model = createSelector([raw.$rawModels, (_state, id) => id], (models, id) => {
  return models[id];
});

export const $currentModel = createSelector(
  raw.$rawCurrentIds,
  raw.$rawModels,
  (currentIds, models) => {
    const { modelId } = currentIds;
    return models[modelId];
  }
);

export const $currentModelId = createSelector(raw.$rawCurrentIds, (currentIds) => {
  const { modelId } = currentIds;
  return modelId as string;
});

export const $currentGenerator = createSelector(
  raw.$rawCurrentIds,
  raw.$rawGenerators,
  (currentIds, generators) => {
    const { generatorId } = currentIds;
    return generators[generatorId];
  }
);

export const $currentGeneratorId = createSelector(raw.$rawCurrentIds, (currentIds) => {
  const { generatorId } = currentIds;
  return generatorId;
});

export const $generator = createSelector(
  [raw.$rawGenerators, (_state, id) => id],
  (generators = {}, id) => {
    return generators[id];
  }
);

export const $generatorByPrevId = createSelector(
  [raw.$rawGenerators, (_state, id) => id],
  (generators, id) => {
    return Object.values(generators).find((g) => g.prevId === id);
  }
);

export const $assetPack = createSelector([raw.$rawAssetPacks, (_state, id) => id], (packs, id) => {
  return packs[id];
});

export const $palette = createSelector([raw.$rawPalettes, (_state, id) => id], (palettes, id) => {
  return palettes[id];
});

export const $paletteByPrevId = createSelector(
  [raw.$rawPalettes, (_state, id) => id],
  (palettes, id) => {
    return Object.values(palettes).find((p) => p.prevId === id);
  }
);

export const $currentPaletteId = createSelector($currentModel, (model) => {
  return model?.paletteId;
});

export const $currentMaterialPack = createSelector($currentModel, (model) => {
  return model?.materials;
});

export const $currentTransparencies = createSelector($currentMaterialPack, (pack) => {
  if (!pack) return [1, 1, 1, 1];

  return Object.values(pack).map((m) => m.transparency ?? 1);
});

export const $currentSlotConfig = createSelector($currentGenerator, (generator) => {
  return generator?.slots;
});

export const $texturedMaterial = createSelector(
  [raw.$rawAllMaterials, raw.$rawSettings, (_state, id) => id],
  (materials, settings, id) => {
    const material = materials[id];
    return parseTexturedMaterial(material, settings.texturesResolution);
  }
);

export const $randomMaterialPack = createSelector(
  [raw.$rawAllMaterials, raw.$rawSettings, $currentGenerator, (_state, id) => id],

  (materials, settings, generator, _id) => {
    if (!generator || !generator.slots?.active) return;
    const { active } = generator.slots;

    const materialsArr = Object.values(materials);
    const s0 = randomArr(materialsArr);
    const s1 = randomArr(materialsArr);
    const s2 = randomArr(materialsArr);
    const s3 = randomArr(materialsArr);

    const m0 = materialsArr.find((m: any) => m.id === s0.id);
    const m1 = materialsArr.find((m: any) => m.id === s1.id);
    const m2 = materialsArr.find((m: any) => m.id === s2.id);
    const m3 = materialsArr.find((m: any) => m.id === s3.id);

    const source: any = {
      0: parseTexturedMaterial(m0, settings.texturesResolution),
      1: parseTexturedMaterial(m1, settings.texturesResolution),
      2: parseTexturedMaterial(m2, settings.texturesResolution),
      3: parseTexturedMaterial(m3, settings.texturesResolution),
    };

    const output: Partial<IMaterialPack> = {};

    active.forEach((slot) => {
      output[slot] = source[slot];
    });

    return output;
  }
);

export const $spawnPack = createSelector(
  [raw.$rawAllMaterials, (_state, p) => p],
  (palettes, p) => {
    return Object.keys(p).reduce((acc, key) => {
      const value = p[key];
      const { id: materialId, tint } = value;
      const material = palettes[materialId];
      if (!material) return acc;

      acc[key] = {
        ...material,
        tint: parseTint(tint),
      };

      return acc;
    }, {} as any);
  }
);
